import React, { Component } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { Add, Delete, Edit, Refresh, Visibility, WarningAmber } from "@mui/icons-material";
import axios from "axios";
import Api from "../../../customlib/Api";

// trigger_key yang dipakai LANGSUNG oleh TeleAutoReplyService — dihapus/dinonaktifkan
// akan membuat pengiriman terkait gagal (exception) saat mode terkait aktif.
const PROTECTED_TRIGGER_KEYS = [
    "tele_single_reply",
    "tele_help_menu",
    "tele_option_info_promo",
    "tele_option_status_pesanan",
    "tele_option_hubungi_sales",
    "tele_option_lainnya",
];

const EMPTY_FORM = {
    trigger_key: "",
    category: "",
    label: "",
    message_template: "",
    hsm_name: "",
    is_active: true,
};

class AutoReplyTeleTemplates extends Component {
    state = {
        loading: false,
        templates: [],
        // Dialog create/edit/view
        dialogOpen: false,
        dialogMode: "create", // create | edit | view
        form: { ...EMPTY_FORM },
        editId: null,
        saving: false,
        // Confirm dialog (delete / deactivate template terproteksi)
        actionConfirmOpen: false,
        actionConfirmMode: null, // "delete" | "deactivate"
        actionConfirmTarget: null,
        actionConfirmBusy: false,
        // Filter
        filterCategory: "",
        filterSearch: "",
        // Snackbar
        snackbar: { open: false, message: "", severity: "success" },
    };

    componentDidMount() {
        this.loadTemplates();
    }

    loadTemplates = () => {
        this.setState({ loading: true });
        axios
            .get(Api.teleAutoReplyListTemplates(), Api.getRequestHeader())
            .then((res) => {
                const data = res.data || {};
                if (data.success) {
                    this.setState({
                        loading: false,
                        templates: (data.data && data.data.templates) || [],
                    });
                } else {
                    this.setState({ loading: false });
                    this.showSnackbar(data.message || "Gagal memuat templates", "error");
                }
            })
            .catch((err) => {
                this.setState({ loading: false });
                this.showSnackbar(err.message || "Network error", "error");
            });
    };

    showSnackbar = (message, severity = "success") => {
        this.setState({ snackbar: { open: true, message, severity } });
    };

    isProtectedKey = (triggerKey) => PROTECTED_TRIGGER_KEYS.includes(triggerKey);

    // ============================================================
    // Dialog create/edit/view
    // ============================================================

    openCreate = () => {
        this.setState({
            dialogOpen: true,
            dialogMode: "create",
            form: { ...EMPTY_FORM },
            editId: null,
        });
    };

    openEdit = (row) => {
        this.setState({
            dialogOpen: true,
            dialogMode: "edit",
            editId: row.id,
            form: {
                trigger_key: row.trigger_key || "",
                category: row.category || "",
                label: row.label || "",
                message_template: row.message_template || "",
                hsm_name: row.hsm_name || "",
                is_active: row.is_active !== false,
            },
        });
    };

    openView = (row) => {
        this.setState({
            dialogOpen: true,
            dialogMode: "view",
            editId: row.id,
            form: {
                trigger_key: row.trigger_key || "",
                category: row.category || "",
                label: row.label || "",
                message_template: row.message_template || "",
                hsm_name: row.hsm_name || "",
                is_active: row.is_active !== false,
            },
        });
    };

    closeDialog = () => {
        this.setState({ dialogOpen: false });
    };

    handleFormChange = (field, value) => {
        this.setState({ form: { ...this.state.form, [field]: value } });
    };

    handleSave = () => {
        const { form, dialogMode, editId } = this.state;

        if (!form.trigger_key.trim()) {
            this.showSnackbar("Trigger Key wajib diisi", "error");
            return;
        }
        if (!form.message_template.trim()) {
            this.showSnackbar("Message Template wajib diisi", "error");
            return;
        }

        this.setState({ saving: true });

        const payload = {
            trigger_key: form.trigger_key.trim(),
            category: form.category.trim() || null,
            label: form.label.trim() || null,
            message_template: form.message_template,
            hsm_name: form.hsm_name.trim() || null,
            is_active: form.is_active,
        };

        const request =
            dialogMode === "create"
                ? axios.post(Api.teleAutoReplyCreateTemplate(), payload, Api.getRequestHeaderJson())
                : axios.put(Api.teleAutoReplyUpdateTemplate(editId), payload, Api.getRequestHeaderJson());

        request
            .then((res) => {
                const data = res.data || {};
                if (data.success) {
                    this.setState({ saving: false, dialogOpen: false });
                    this.showSnackbar(
                        dialogMode === "create" ? "Template berhasil dibuat" : "Template berhasil diupdate",
                        "success"
                    );
                    this.loadTemplates();
                } else {
                    this.setState({ saving: false });
                    this.showSnackbar(data.message || "Gagal menyimpan", "error");
                }
            })
            .catch((err) => {
                this.setState({ saving: false });
                this.showSnackbar(
                    (err.response && err.response.data && err.response.data.message) ||
                        err.message ||
                        "Network error",
                    "error"
                );
            });
    };

    // ============================================================
    // Delete & toggle active — dengan pengaman untuk trigger_key terproteksi
    // ============================================================

    openDeleteConfirm = (row) => {
        this.setState({
            actionConfirmOpen: true,
            actionConfirmMode: "delete",
            actionConfirmTarget: row,
        });
    };

    requestToggleActive = (row) => {
        const willDeactivate = row.is_active !== false;
        if (willDeactivate && this.isProtectedKey(row.trigger_key)) {
            this.setState({
                actionConfirmOpen: true,
                actionConfirmMode: "deactivate",
                actionConfirmTarget: row,
            });
            return;
        }
        this.toggleActive(row);
    };

    closeActionConfirm = () => {
        this.setState({ actionConfirmOpen: false, actionConfirmMode: null, actionConfirmTarget: null });
    };

    confirmAction = () => {
        const { actionConfirmMode, actionConfirmTarget } = this.state;
        if (!actionConfirmTarget) return;

        if (actionConfirmMode === "delete") {
            this.performDelete(actionConfirmTarget);
        } else if (actionConfirmMode === "deactivate") {
            this.toggleActive(actionConfirmTarget);
            this.closeActionConfirm();
        }
    };

    performDelete = (target) => {
        this.setState({ actionConfirmBusy: true });
        axios
            .delete(Api.teleAutoReplyDeleteTemplate(target.id), Api.getRequestHeader())
            .then((res) => {
                const data = res.data || {};
                if (data.success) {
                    this.setState({ actionConfirmBusy: false });
                    this.closeActionConfirm();
                    this.showSnackbar("Template berhasil dihapus", "success");
                    this.loadTemplates();
                } else {
                    this.setState({ actionConfirmBusy: false });
                    this.showSnackbar(data.message || "Gagal menghapus", "error");
                }
            })
            .catch((err) => {
                this.setState({ actionConfirmBusy: false });
                this.showSnackbar(err.message || "Network error", "error");
            });
    };

    toggleActive = (row) => {
        const payload = { is_active: !row.is_active };
        axios
            .put(Api.teleAutoReplyUpdateTemplate(row.id), payload, Api.getRequestHeaderJson())
            .then((res) => {
                const data = res.data || {};
                if (data.success) {
                    this.loadTemplates();
                } else {
                    this.showSnackbar(data.message || "Gagal toggle", "error");
                }
            })
            .catch((err) => {
                this.showSnackbar(err.message || "Network error", "error");
            });
    };

    // ============================================================
    // Filter
    // ============================================================

    getCategoryOptions = () => {
        const set = new Set();
        (this.state.templates || []).forEach((t) => {
            if (t.category) set.add(t.category);
        });
        return Array.from(set);
    };

    getFilteredTemplates = () => {
        const { templates, filterCategory, filterSearch } = this.state;
        let filtered = templates;

        if (filterCategory) {
            filtered = filtered.filter((t) => t.category === filterCategory);
        }
        if (filterSearch.trim()) {
            const q = filterSearch.toLowerCase();
            filtered = filtered.filter(
                (t) =>
                    (t.trigger_key || "").toLowerCase().includes(q) ||
                    (t.label || "").toLowerCase().includes(q) ||
                    (t.message_template || "").toLowerCase().includes(q)
            );
        }
        return filtered;
    };

    // ============================================================
    // Render
    // ============================================================

    render() {
        const {
            loading,
            snackbar,
            dialogOpen,
            dialogMode,
            form,
            saving,
            actionConfirmOpen,
            actionConfirmMode,
            actionConfirmTarget,
            actionConfirmBusy,
            filterCategory,
            filterSearch,
        } = this.state;

        const filtered = this.getFilteredTemplates();
        const categoryOptions = this.getCategoryOptions();

        return (
            <Box>
                {/* Header */}
                <Grid container spacing={1} alignItems="center">
                    <Grid item sm={9} xs={7}>
                        <Typography variant="h6" fontWeight={600}>
                            Tele Auto-Reply Templates
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Kelola isi pesan yang dikirim fitur Tele Auto Reply ke telecustomer.
                        </Typography>
                    </Grid>
                    <Grid item sm={3} xs={5} textAlign="right">
                        <Tooltip title="Reload">
                            <span>
                                <IconButton onClick={this.loadTemplates} disabled={loading}>
                                    <Refresh />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Grid>
                </Grid>
                <Divider sx={{ mb: 2, mt: 1 }} />

                {loading && <LinearProgress sx={{ mb: 2 }} />}

                <Alert severity="warning" icon={<WarningAmber fontSize="inherit" />} sx={{ mb: 2 }}>
                    6 trigger key (<code>tele_single_reply</code>, <code>tele_help_menu</code>, <code>tele_option_*</code>) dipakai langsung
                    oleh sistem. Menghapus atau menonaktifkannya akan membuat pengiriman terkait gagal saat mode auto-reply aktif.
                </Alert>

                {/* Toolbar: filter + add */}
                <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
                    <TextField
                        size="small"
                        placeholder="Cari trigger / label / isi..."
                        value={filterSearch}
                        onChange={(e) => this.setState({ filterSearch: e.target.value })}
                        sx={{ minWidth: 220 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Kategori</InputLabel>
                        <Select
                            value={filterCategory}
                            label="Kategori"
                            onChange={(e) => this.setState({ filterCategory: e.target.value })}
                        >
                            <MenuItem value="">Semua</MenuItem>
                            {categoryOptions.map((c) => (
                                <MenuItem key={c} value={c}>
                                    {c}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button variant="contained" startIcon={<Add />} onClick={this.openCreate}>
                        Tambah Template
                    </Button>
                </Box>

                {/* Table */}
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: "grey.100" }}>
                                <TableCell width={40}>#</TableCell>
                                <TableCell>Trigger Key</TableCell>
                                <TableCell>Label</TableCell>
                                <TableCell>Kategori</TableCell>
                                <TableCell>Pesan (preview)</TableCell>
                                <TableCell width={80} align="center">Aktif</TableCell>
                                <TableCell width={130} align="center">Aksi</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtered.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        <Typography variant="body2" color="text.secondary" py={3}>
                                            {loading ? "Memuat..." : "Tidak ada template ditemukan."}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {filtered.map((row, idx) => (
                                <TableRow key={row.id} hover>
                                    <TableCell>{idx + 1}</TableCell>
                                    <TableCell>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                            <Typography variant="body2" fontFamily="monospace" fontSize={12}>
                                                {row.trigger_key}
                                            </Typography>
                                            {this.isProtectedKey(row.trigger_key) && (
                                                <Tooltip title="Dipakai langsung oleh sistem — hati-hati saat hapus/nonaktifkan">
                                                    <WarningAmber color="warning" sx={{ fontSize: 15 }} />
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                            {row.label || "-"}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {row.category ? <Chip size="small" label={row.category} variant="outlined" /> : "-"}
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="body2"
                                            noWrap
                                            sx={{ maxWidth: 280, color: "text.secondary" }}
                                        >
                                            {(row.message_template || "").substring(0, 80)}
                                            {(row.message_template || "").length > 80 ? "..." : ""}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Switch
                                            size="small"
                                            checked={row.is_active !== false}
                                            onChange={() => this.requestToggleActive(row)}
                                            color="success"
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Lihat">
                                            <IconButton size="small" onClick={() => this.openView(row)}>
                                                <Visibility fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Edit">
                                            <IconButton size="small" color="primary" onClick={() => this.openEdit(row)}>
                                                <Edit fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Hapus">
                                            <IconButton size="small" color="error" onClick={() => this.openDeleteConfirm(row)}>
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                    Total: {filtered.length} template
                    {filterCategory || filterSearch.trim() ? ` (filtered dari ${this.state.templates.length})` : ""}
                </Typography>

                {/* Create/Edit/View Dialog */}
                <Dialog open={dialogOpen} onClose={this.closeDialog} maxWidth="md" fullWidth>
                    <DialogTitle>
                        {dialogMode === "create"
                            ? "Tambah Template Baru"
                            : dialogMode === "edit"
                            ? "Edit Template"
                            : "Detail Template"}
                    </DialogTitle>
                    <DialogContent dividers>
                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Trigger Key *"
                                    value={form.trigger_key}
                                    onChange={(e) => this.handleFormChange("trigger_key", e.target.value)}
                                    disabled={dialogMode === "view" || dialogMode === "edit"}
                                    helperText={
                                        dialogMode === "edit"
                                            ? "Trigger Key tidak bisa diubah lewat form ini"
                                            : "Identifier unik, harus berbeda dari template lain (contoh: tele_option_promo_lebaran)"
                                    }
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Kategori"
                                    value={form.category}
                                    onChange={(e) => this.handleFormChange("category", e.target.value)}
                                    disabled={dialogMode === "view"}
                                    placeholder="single, menu, menu_option, dst (bebas)"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={8}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Label (judul di UI admin)"
                                    value={form.label}
                                    onChange={(e) => this.handleFormChange("label", e.target.value)}
                                    disabled={dialogMode === "view"}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="HSM Name (vendor)"
                                    value={form.hsm_name}
                                    onChange={(e) => this.handleFormChange("hsm_name", e.target.value)}
                                    disabled={dialogMode === "view"}
                                    placeholder="Opsional"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={6}
                                    label="Message Template *"
                                    value={form.message_template}
                                    onChange={(e) => this.handleFormChange("message_template", e.target.value)}
                                    disabled={dialogMode === "view"}
                                    helperText="Gunakan {{customer_wa}} & {{sales_wa}} untuk placeholder."
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={form.is_active}
                                            onChange={(e) => this.handleFormChange("is_active", e.target.checked)}
                                            disabled={dialogMode === "view"}
                                            color="success"
                                        />
                                    }
                                    label="Aktif"
                                />
                                {this.isProtectedKey(form.trigger_key) && (
                                    <Alert severity="warning" icon={<WarningAmber fontSize="inherit" />} sx={{ mt: 1 }}>
                                        Trigger key ini dipakai langsung oleh sistem. Menonaktifkannya akan membuat pengiriman terkait gagal.
                                    </Alert>
                                )}
                            </Grid>
                            {dialogMode === "view" && (
                                <Grid item xs={12}>
                                    <Alert severity="info" variant="outlined">
                                        <Typography variant="subtitle2" fontWeight={600}>Preview Pesan:</Typography>
                                        <Box
                                            sx={{
                                                mt: 1,
                                                p: 1.5,
                                                bgcolor: "#e8f5e9",
                                                borderRadius: 2,
                                                whiteSpace: "pre-wrap",
                                                fontFamily: "monospace",
                                                fontSize: 13,
                                            }}
                                        >
                                            {form.message_template || "(kosong)"}
                                        </Box>
                                    </Alert>
                                </Grid>
                            )}
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.closeDialog} color="inherit">
                            {dialogMode === "view" ? "Tutup" : "Batal"}
                        </Button>
                        {dialogMode !== "view" && (
                            <Button
                                variant="contained"
                                onClick={this.handleSave}
                                disabled={saving}
                                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                            >
                                {saving ? "Menyimpan..." : dialogMode === "create" ? "Buat" : "Simpan"}
                            </Button>
                        )}
                    </DialogActions>
                </Dialog>

                {/* Confirm Dialog: delete / deactivate trigger key terproteksi */}
                <Dialog open={actionConfirmOpen} onClose={this.closeActionConfirm} maxWidth="xs">
                    <DialogTitle>
                        {actionConfirmMode === "delete" ? "Hapus Template?" : "Nonaktifkan Template?"}
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body2">
                            Yakin ingin {actionConfirmMode === "delete" ? "menghapus" : "menonaktifkan"} template{" "}
                            <strong>{actionConfirmTarget && actionConfirmTarget.trigger_key}</strong>?
                        </Typography>
                        <Alert severity="warning" icon={<WarningAmber fontSize="inherit" />} sx={{ mt: 2 }}>
                            Trigger key ini dipakai langsung oleh sistem. Pengiriman pesan terkait akan{" "}
                            <b>gagal (exception)</b> saat mode auto-reply terkait masih aktif.
                        </Alert>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.closeActionConfirm} color="inherit" disabled={actionConfirmBusy}>
                            Batal
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={this.confirmAction}
                            disabled={actionConfirmBusy}
                            startIcon={
                                actionConfirmBusy ? (
                                    <CircularProgress size={16} color="inherit" />
                                ) : actionConfirmMode === "delete" ? (
                                    <Delete />
                                ) : null
                            }
                        >
                            {actionConfirmBusy
                                ? "Memproses..."
                                : actionConfirmMode === "delete"
                                ? "Hapus"
                                : "Nonaktifkan"}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Snackbar */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={() => this.setState({ snackbar: { ...snackbar, open: false } })}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                >
                    <Alert
                        severity={snackbar.severity}
                        onClose={() => this.setState({ snackbar: { ...snackbar, open: false } })}
                        sx={{ width: "100%" }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        );
    }
}

export default AutoReplyTeleTemplates;
